import type React from "react";
import { Suspense, useMemo } from "react";
import { AuthProvider, type AuthProviderProps } from "react-oidc-context";
import { RouterProvider } from "react-router-dom";
import { Log } from "oidc-client-ts";

import { router } from "@/base/components/AppRouter";
import { Loader } from "@/base/components/Global";
import MetaTags from "@/base/components/MetaTags";
import NoAuthProvider from "@/base/components/NoAuthProvider";
import useLocaleOverride from "@/base/hooks/useLocaleOverride";
import { getSSOClientId } from "@/base/utils/authUtils";
import LoyaltyWebStorageStateStore from "@/base/utils/LoyaltyWebStorageStateStore";
import config from "@/constants/config";
import { useAppSelector } from "@/store/hooks";
import { selectCurrentLanguage } from "@/store/language";
import { selectCurrentRegionPrefix } from "@/store/region";

if (process.env.NODE_ENV === "development") {
  Log.setLogger(console);
}

const isAuthEnabled =
  config.routerConfig.routerMode === "profile" ||
  config.routerConfig.routerMode === "giftCard";

const AuthenticatedApp: React.FC = () => {
  const currentRegionPrefix = useAppSelector(selectCurrentRegionPrefix);
  const currentLanguage = useAppSelector(selectCurrentLanguage);

  const oidcSettings: AuthProviderProps = useMemo(
    () => ({
      authority: config.ssoConfig.authorityUrl,
      client_id: getSSOClientId(currentRegionPrefix),
      redirect_uri: globalThis.origin,
      silent_redirect_uri: `${globalThis.origin}/silent-renew.html`,
      response_type: "code",
      scope:
        "openid profile customer.api.read customer.api.write offline_access",
      post_logout_redirect_uri: globalThis.origin,
      automaticSilentRenew: true,
      redirectMethod: "replace",
      onSigninCallback: () => {
        const url = new URL(window.location.href);
        url.searchParams.delete("code");
        url.searchParams.delete("scope");
        url.searchParams.delete("state");
        url.searchParams.delete("session_state");
        url.searchParams.delete("iss");
        window.history.replaceState({}, "", url.pathname + url.search);
      },
      acr_values: `culture:${currentLanguage}`,
      userStore: new LoyaltyWebStorageStateStore({
        prefix: config.computed.authKeyPrefix,
        store: globalThis.localStorage,
      }),
    }),
    [currentRegionPrefix, currentLanguage],
  );

  return (
    <AuthProvider {...oidcSettings}>
      <RouterProvider router={router} />
    </AuthProvider>
  );
};

const App: React.FC = () => {
  if (isAuthEnabled) {
    return <AuthenticatedApp />;
  }

  return (
    <NoAuthProvider>
      <RouterProvider router={router} />
    </NoAuthProvider>
  );
};

// We need to load the language and region overrides before we initialize the AuthProvider, otherwise we won't load the correct region client ID
const AppLoader: React.FC = () => {
  const { isLocaleLoaded } = useLocaleOverride();

  if (!isLocaleLoaded) {
    return <Loader />;
  }

  return (
    <Suspense fallback={<Loader />}>
      <MetaTags />
      <App />
    </Suspense>
  );
};

export default AppLoader;
